import { FormulaEquation } from '../../types';

export const KINEMATICS_EQUATIONS: FormulaEquation[] = [
  {
    id: 'delta_v',
    name: 'Definição de Variação de Velocidade',
    subject: 'FISICA',
    latex: 'v = v_0 + \\Delta v \\iff \\Delta v = v - v_0',
    variables: ['v', 'v0', 'deltav'],
    description: 'A velocidade final é a soma da velocidade inicial com a variação de velocidade sofrida pelo corpo.',
    rearrangedLatex: {
      deltav: '\\Delta v = v - v_0',
      v: 'v = v_0 + \\Delta v',
      v0: 'v_0 = v - \\Delta v',
    },
    descriptorCode: 'FIS_D3',
  },
  {
    id: 'aceleracao_media',
    name: 'Aceleração Escalar Média',
    subject: 'FISICA',
    latex: 'a = \\frac{\\Delta v}{\\Delta t} \\iff \\Delta v = a \\cdot \\Delta t',
    variables: ['a', 'deltav', 'deltat'],
    description: 'Razão entre a variação da velocidade escalar e o intervalo de tempo decorrido.',
    rearrangedLatex: {
      a: 'a = \\frac{\\Delta v}{\\Delta t}',
      deltav: '\\Delta v = a \\cdot \\Delta t',
      deltat: '\\Delta t = \\frac{\\Delta v}{a}',
    },
    descriptorCode: 'FIS_D3',
  },
  {
    id: 'muv_vel',
    name: 'Função Horária da Velocidade (MUV)',
    subject: 'FISICA',
    latex: 'v = v_0 + a \\cdot \\Delta t',
    variables: ['v', 'v0', 'a', 'deltat'],
    description: 'Calcula a velocidade final no MUV em função da aceleração e do tempo decorrido.',
    rearrangedLatex: {
      v: 'v = v_0 + a \\cdot \\Delta t',
      v0: 'v_0 = v - a \\cdot \\Delta t',
      a: 'a = \\frac{v - v_0}{\\Delta t}',
      deltat: '\\Delta t = \\frac{v - v_0}{a}',
    },
    descriptorCode: 'FIS_D3',
  },
  {
    id: 'torricelli',
    name: 'Equação de Torricelli',
    subject: 'FISICA',
    latex: 'v^2 = v_0^2 + 2a\\Delta S',
    variables: ['v', 'v0', 'a', 'deltaS'],
    description: 'Relaciona velocidades inicial e final com aceleração e deslocamento escalar, sem depender do tempo.',
    rearrangedLatex: {
      v: 'v = \\sqrt{v_0^2 + 2a\\Delta S}',
      v0: 'v_0 = \\sqrt{v^2 - 2a\\Delta S}',
      a: 'a = \\frac{v^2 - v_0^2}{2\\Delta S}',
      deltaS: '\\Delta S = \\frac{v^2 - v_0^2}{2a}',
    },
    descriptorCode: 'FIS_D3',
  },
  {
    id: 'delta_s',
    name: 'Deslocamento Escalar (Variação de Posição)',
    subject: 'FISICA',
    latex: '\\Delta S = S - S_0 \\iff S = S_0 + \\Delta S',
    variables: ['deltaS', 'S', 'S0'],
    description: 'Variação escalar entre a posição final e a posição inicial.',
    rearrangedLatex: {
      deltaS: '\\Delta S = S - S_0',
      S: 'S = S_0 + \\Delta S',
      S0: 'S_0 = S - \\Delta S',
    },
    descriptorCode: 'FIS_D1',
  },
  {
    id: 'mu_deslocamento',
    name: 'Deslocamento no Movimento Uniforme (MU)',
    subject: 'FISICA',
    latex: '\\Delta S = v \\cdot \\Delta t',
    variables: ['deltaS', 'v', 'deltat'],
    description: 'No movimento com velocidade constante, o deslocamento é a velocidade multiplicada pelo tempo.',
    rearrangedLatex: {
      deltaS: '\\Delta S = v \\cdot \\Delta t',
      v: 'v = \\frac{\\Delta S}{\\Delta t}',
      deltat: '\\Delta t = \\frac{\\Delta S}{v}',
    },
    descriptorCode: 'FIS_D3',
  },
  {
    id: 'mu_pos',
    name: 'Função Horária da Posição (MU)',
    subject: 'FISICA',
    latex: 'S = S_0 + v \\cdot \\Delta t',
    variables: ['S', 'S0', 'v', 'deltat'],
    description: 'Posição final em função da posição inicial, velocidade e tempo no movimento uniforme.',
    rearrangedLatex: {
      S: 'S = S_0 + v \\cdot \\Delta t',
      S0: 'S_0 = S - v \\cdot \\Delta t',
      v: 'v = \\frac{S - S_0}{\\Delta t}',
      deltat: '\\Delta t = \\frac{S - S_0}{v}',
    },
    descriptorCode: 'FIS_D1',
  },
  {
    id: 'muv_delta_s',
    name: 'Deslocamento no MUV',
    subject: 'FISICA',
    latex: '\\Delta S = v_0 \\Delta t + \\frac{1}{2}a(\\Delta t)^2',
    variables: ['deltaS', 'v0', 'a', 'deltat'],
    description: 'Deslocamento percorrido sob aceleração constante a partir da velocidade inicial.',
    rearrangedLatex: {
      deltaS: '\\Delta S = v_0 \\Delta t + \\frac{1}{2}a(\\Delta t)^2',
      v0: 'v_0 = \\frac{\\Delta S - \\frac{1}{2}a(\\Delta t)^2}{\\Delta t}',
      a: 'a = \\frac{2(\\Delta S - v_0 \\Delta t)}{(\\Delta t)^2}',
      deltat: '\\Delta t = \\frac{-v_0 + \\sqrt{v_0^2 + 2a\\Delta S}}{a}',
    },
    descriptorCode: 'FIS_D3',
  },
  {
    id: 'muv_pos',
    name: 'Função Horária da Posição (MUV)',
    subject: 'FISICA',
    latex: 'S = S_0 + v_0 \\Delta t + \\frac{1}{2}a(\\Delta t)^2',
    variables: ['S', 'S0', 'v0', 'a', 'deltat'],
    description: 'Posição no espaço em função do tempo sob aceleração constante.',
    rearrangedLatex: {
      S: 'S = S_0 + v_0 \\Delta t + \\frac{1}{2}a(\\Delta t)^2',
      S0: 'S_0 = S - v_0 \\Delta t - \\frac{1}{2}a(\\Delta t)^2',
      v0: 'v_0 = \\frac{S - S_0 - \\frac{1}{2}a(\\Delta t)^2}{\\Delta t}',
      a: 'a = \\frac{2(S - S_0 - v_0 \\Delta t)}{(\\Delta t)^2}',
      deltat: '\\Delta t = \\frac{-v_0 + \\sqrt{v_0^2 + 2a(S - S_0)}}{a}',
    },
    descriptorCode: 'FIS_D1',
  },
  {
    id: 'vel_media',
    name: 'Definição de Velocidade Média',
    subject: 'FISICA',
    latex: 'v_m = \\frac{\\Delta S}{\\Delta t}',
    variables: ['vm', 'deltaS', 'deltat'],
    description: 'Razão entre o deslocamento escalar total e o intervalo de tempo transcorrido.',
    rearrangedLatex: {
      vm: 'v_m = \\frac{\\Delta S}{\\Delta t}',
      deltaS: '\\Delta S = v_m \\cdot \\Delta t',
      deltat: '\\Delta t = \\frac{\\Delta S}{v_m}',
    },
    descriptorCode: 'FIS_D3',
  },
  {
    id: 'vel_media_muv',
    name: 'Velocidade Média no MUV',
    subject: 'FISICA',
    latex: 'v_m = \\frac{v_0 + v}{2}',
    variables: ['vm', 'v0', 'v'],
    description: 'No MUV, a velocidade média escalar equivale à média aritmética das velocidades inicial e final.',
    rearrangedLatex: {
      vm: 'v_m = \\frac{v_0 + v}{2}',
      v: 'v = 2v_m - v_0',
      v0: 'v_0 = 2v_m - v',
    },
    descriptorCode: 'FIS_D3',
  },
  {
    id: 'muv_delta_s_sem_a',
    name: 'Deslocamento no MUV (sem aceleração)',
    subject: 'FISICA',
    latex: '\\Delta S = \\left(\\frac{v_0 + v}{2}\\right) \\Delta t',
    variables: ['deltaS', 'v0', 'v', 'deltat'],
    description: 'Deslocamento no MUV utilizando apenas velocidades inicial e final e tempo gasto.',
    rearrangedLatex: {
      deltaS: '\\Delta S = \\left(\\frac{v_0 + v}{2}\\right) \\Delta t',
      deltat: '\\Delta t = \\frac{2\\Delta S}{v_0 + v}',
      v: 'v = \\frac{2\\Delta S}{\\Delta t} - v_0',
      v0: 'v_0 = \\frac{2\\Delta S}{\\Delta t} - v',
    },
    descriptorCode: 'FIS_D3',
  },
];
